import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListItem } from '../../types/list';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { AppStoreIcon, GooglePlayIcon } from '../icons';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { useSection } from '../../hooks/useSection';
import { useAutoTranslation } from '../../hooks/useAutoTranslation';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { logger } from '../../lib/logger';
import { favoritesService } from '../../lib/favorites/favoritesService';

interface DetailCardProps {
  item: ListItem;
  items: ListItem[];
  onClose: () => void;
  onItemChange: (item: ListItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const currentLanguage = i18n.language;
  const [isFavorite, setIsFavorite] = useState(() => favoritesService.isFavorite(item.id));
  const [translatedDescriptionState, setTranslatedDescriptionState] = useState<string | null>(null);
  const [translatedOfferState, setTranslatedOfferState] = useState<string | null>(null);
  
  // Use the useImageDisplay hook to determine if image should be displayed
  const { displayImage, shouldDisplay } = useImageDisplay(item.image, 'detailCard');

  // Log when item has an offer
  useEffect(() => {
    if (item.offer) {
      logger.debug('Liste item has offer', {
        category: 'Offer',
        data: {
          itemId: item.id,
          itemName: item.name,
          offerLength: item.offer.length,
          offerPreview: item.offer.substring(0, 50) + (item.offer.length > 50 ? '...' : ''),
          hasOffers: !!item.offers,
          offersLanguages: item.offers ? Object.keys(item.offers) : []
        }
      });
    }
  }, [item.id, item.name, item.offer, item.offers]);

  // Utiliser le hook useAutoTranslation pour la description
  const { 
    translate: translateDescription, 
    translating: translatingDescription, 
    error: descriptionError 
  } = useAutoTranslation({
    description: item.description,
    currentLanguage,
    context: {
      tableId,
      itemId: item.id,
      sectionType: 'liste',
      contentType: 'description'
    }
  });

  // Utiliser le hook useAutoTranslation pour l'offre si elle existe
  const { 
    translate: translateOffer, 
    translating: translatingOffer, 
    error: offerError 
  } = useAutoTranslation({
    description: item.offer || '',
    currentLanguage,
    context: {
      tableId,
      itemId: item.id,
      sectionType: 'liste',
      contentType: 'offer'
    }
  });

  // Effectuer la traduction de la description quand la langue change
  useEffect(() => {
    const checkAndTranslateDescription = async () => {
      const currentLang = i18n.language;
      
      // Vérifier si la traduction est manquante ou vide
      const currentDescription = item.descriptions?.[currentLang];
      const isDescriptionMissing = !currentDescription || 
                                 currentDescription.trim() === '' || 
                                 currentDescription === '\n';

      if (isDescriptionMissing) {
        const translated = await translateDescription();
        if (translated) {
          // Mettre à jour les descriptions de l'item
          if (!item.descriptions) {
            item.descriptions = {};
          }
          item.descriptions[currentLang] = translated;
          setTranslatedDescriptionState(translated);
          
          logger.debug('Description translation completed for list item', {
            category: 'Translation',
            data: { 
              itemId: item.id,
              itemName: item.name,
              language: currentLang,
              contentType: 'description',
              translatedLength: translated.length
            }
          });
        }
      }
    };

    checkAndTranslateDescription();
  }, [item.descriptions, i18n.language, translateDescription, item.id, item.name]);

  // Effectuer la traduction de l'offre quand la langue change (si elle existe)
  useEffect(() => {
    const checkAndTranslateOffer = async () => {
      // Skip if no offer exists
      if (!item.offer) return;
      
      const currentLang = i18n.language;
      
      // Initialize offers object if it doesn't exist
      if (!item.offers) {
        item.offers = {};
      }
      
      // Vérifier si la traduction est manquante ou vide
      const currentOffer = item.offers[currentLang];
      const isOfferMissing = !currentOffer || 
                          currentOffer.trim() === '' || 
                          currentOffer === '\n';

      if (isOfferMissing) {
        logger.debug('Processing offer for translation', {
          category: 'Offer',
          data: {
            itemId: item.id,
            itemName: item.name,
            offerLength: item.offer.length,
            offerPreview: item.offer.substring(0, 50) + (item.offer.length > 50 ? '...' : ''),
            currentLanguage
          }
        });
        const translated = await translateOffer();
        if (translated) {
          // Mettre à jour les offres de l'item
          item.offers[currentLang] = translated;
          setTranslatedOfferState(translated);
          
          logger.debug('Offer translation completed for list item', {
            category: 'Translation',
            data: { 
              itemId: item.id,
              itemName: item.name,
              language: currentLang,
              contentType: 'offer',
              translatedLength: translated.length
            }
          });
        }
      }
    };

    if (item.offer) {
      checkAndTranslateOffer();
    }
  }, [item.offers, item.offer, i18n.language, translateOffer, item.id, item.name, currentLanguage]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const renderContent = () => null;

  const renderBookingButton = () => {
    if (!item.appstore && !item.playstore) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2 mb-4">
        {item.appstore && (
          <a
            href={item.appstore}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              analyticsService.trackItemAction(
                section.id,
                item.id,
                'app_store_click',
                'list',
                {
                  item_name: item.name,
                  category: item.category,
                  type: item.type,
                  url: item.appstore,
                  section_name: section.names.fr || 'Unknown Section',
                  section_type: 'liste'
                }
              );
            }}
            className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: config['major-color'] }}
          >
            <AppStoreIcon size={18} className="mr-2" />
            {getTranslatedText('btn_app_store')}
          </a>
        )}
        {item.playstore && (
          <a
            href={item.playstore}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              analyticsService.trackItemAction(
                section.id,
                item.id,
                'play_store_click',
                'list',
                {
                  item_name: item.name,
                  category: item.category,
                  type: item.type,
                  url: item.playstore,
                  section_name: section.names.fr || 'Unknown Section',
                  section_type: 'liste'
                }
              );
            }}
            className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: config['major-color'] }}
          >
            <GooglePlayIcon size={18} className="mr-2" />
            {getTranslatedText('btn_play_store')}
          </a>
        )}
      </div>
    );
  };

  const handleFavoriteToggle = async (newState: boolean) => {
    if (newState) {
      favoritesService.addFavorite({
        id: item.id,
        sourceType: 'liste',
        data: item,
        metadata: {
          sourceSection: {
            id: section.id,
            type_rubrique: 'liste',
            names: section.names
          },
          sectionSlug: section.names.fr,
          sectionNames: section.names,
          item_id: item.id,
          section_id: section.id
        }
      });
      
      analyticsService.trackItemAction(
        section.id,
        item.id,
        'add_favorite',
        'list',
        {
          item_name: item.name,
          category: item.category,
          type: item.type,
          section_name: section.names.fr || 'Unknown Section',
          section_type: 'liste'
        }
      );
    } else {
      favoritesService.removeFavorite(item.id);
      
      analyticsService.trackItemAction(
        section.id,
        item.id,
        'remove_favorite',
        'list',
        {
          item_name: item.name,
          category: item.category,
          type: item.type,
          section_name: section.names.fr || 'Unknown Section',
          section_type: 'liste'
        }
      );
    }
    setIsFavorite(newState);
  };

  // Obtenir la description à afficher
  const getDescription = () => {
    if (translatedDescriptionState) return translatedDescriptionState;
    if (item.descriptions[currentLanguage]) return item.descriptions[currentLanguage];
    return item.description;
  };

  // Obtenir l'offre à afficher
  const getOffer = () => {
    if (translatedOfferState) return translatedOfferState;
    if (item.offers && item.offers[currentLanguage]) return item.offers[currentLanguage];
    return item.offer;
  };

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={getTranslatedCategory}
      getTranslatedText={getTranslatedText}
      context={{
        sectionType: 'liste',
        itemId: item.id,
        section_id: section.id,
        contentType: 'description'
      }}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
      description={getDescription()}
      offer={getOffer()}
      translating={translatingDescription}
      translationError={descriptionError}
    />
  );
};

export default DetailCard;